import {
  Avatar,
  Box,
  BoxProps,
  Button,
  Divider,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  VStack
} from '@chakra-ui/react'
import { BarChart3, Info, PlusIcon } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FiGrid, FiMessageSquare, FiSearch } from 'react-icons/fi'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { CLIENT_CONFIG } from '../../core/client/buildConfig'
import { useAgents } from '../../hooks/agents/useAgents'
import { useUser } from '../../hooks/auth/useUser'
import GeneralInformationModal from '../GeneralInformationModal'

import { useInView } from 'react-intersection-observer'
import { useChats } from '../../hooks/chat/useChats'
import { useDeleteConversation } from '../../hooks/chat/useDeleteConversation'
import { useRenameConversation } from '../../hooks/chat/useRenameConversation'
import { useLanguage } from '../../hooks/utility/useLanguage'
import useMobile from '../../hooks/utility/useMobile'
import { ChatSummarySchema } from '../../types/chatTypes'
import { getIconByName } from '../../utils/iconUtils'
import { sanitizeNameForInitials } from '../../utils/util'
import ConversationRow from './ConversationRow'
import SidebarItem from './SidebarItem'
import SidebarSection from './SidebarSection'

// Unified spacing constants for consistent alignment
const SIDEBAR_SPACING = {
  base: 3, // 12px - base horizontal spacing for the sidebar
  nested: 3, // 12px - additional offset for nested items (relative to base)
}

interface ChatSideBarProps extends BoxProps {
  isLoadingDetails?: boolean
  onNewChat: () => void
  selectedConversationId?: string
  onSelectConversation: (id: string, agentId?: string) => void
  isSidebarOpenExternal?: boolean
  onSidebarToggle?: () => void
  isMinimized?: boolean
  onClearConversation?: () => void
  onSelectAgent?: (agentId: string) => void
}

// Helper function to determine which time period a chat belongs to
function getChatTimePeriod(timestamp: string): 'today' | 'thisWeek' | 'lastWeek' | 'earlier' {
  const chatDate = new Date(timestamp)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  // Set to beginning of day for accurate comparison
  today.setHours(0, 0, 0, 0)
  yesterday.setHours(0, 0, 0, 0)

  // Calculate start of this week (Monday)
  const thisWeekStart = new Date(today)
  const dayOfWeek = today.getDay() || 7 // Convert Sunday (0) to 7
  thisWeekStart.setDate(today.getDate() - dayOfWeek + 1)

  // Calculate start of last week
  const lastWeekStart = new Date(thisWeekStart)
  lastWeekStart.setDate(lastWeekStart.getDate() - 7)

  // Determine period
  if (chatDate >= today) {
    return 'today'
  } else if (chatDate >= thisWeekStart) {
    return 'thisWeek'
  } else if (chatDate >= lastWeekStart) {
    return 'lastWeek'
  } else {
    return 'earlier'
  }
}

const slugToIcon = (slug: string, iconColor: string, isDarkMode: boolean) => {
  switch (slug) {
    default:
      return null
  }
}

const getIcon = (chat: ChatSummarySchema, iconColor: string, isDarkMode: boolean) => {
  if (chat.agentIcon && !chat.agentSlug) {
    return getIconByName(chat.agentIcon)
  } else if (chat.agentSlug) {
    return slugToIcon(chat.agentSlug, iconColor, isDarkMode)
  }
  return null
}

const ChatSidebar: React.FC<ChatSideBarProps> = ({
  onNewChat,
  isLoadingDetails,
  selectedConversationId: selectedChatId,
  onSelectConversation: onSelectChat,
  isSidebarOpenExternal,
  onSidebarToggle,
  isMinimized,
  onClearConversation,
  onSelectAgent,
  ...props
}) => {
  const navigate = useNavigate()
  const isMobile = useMobile()
  const { isOpen: isOpenInternal, onClose: onCloseInternal } = useDisclosure({ defaultIsOpen: !isMobile })
  const {
    isOpen: isInformationModalOpen,
    onOpen: openInformationModal,
    onClose: closeInformationModal,
  } = useDisclosure()
  const [searchQuery, setSearchQuery] = useState('')
  // const { user } = useAppContext();
  const { t } = useLanguage()

  const { agentId } = useParams()

  // Use the unified chats hook with search functionality
  const {
    chatList: chats,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useChats({ searchQuery })

  const { renameConversation } = useRenameConversation()
  const { deleteConversation } = useDeleteConversation()

  const { agents, agentError, isLoadingAgents } = useAgents()
  const { user } = useAuth()
  const { data: currentUser } = useUser()

  // Color mode values
  const { colorMode } = useColorMode()
  const sidebarBg = useColorModeValue('sidebar.light', 'sidebar.dark')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const iconColor = useColorModeValue('gray.600', 'gray.300')
  const countColor = useColorModeValue('gray.500', 'gray.400')
  const placeholderColor = useColorModeValue('gray.500', 'gray.400')
  const isDarkMode = colorMode === 'dark'

  // Determine whether to use internal or external state management
  const isExternallyControlled = isSidebarOpenExternal !== undefined && onSidebarToggle !== undefined
  const isOpen = isExternallyControlled ? isSidebarOpenExternal : isOpenInternal

  const closeSidebarOnMobile = useCallback(() => {
    if (!isMobile || !isOpen) return
    if (isExternallyControlled) {
      onSidebarToggle?.()
    } else {
      onCloseInternal()
    }
  }, [isMobile, isOpen, isExternallyControlled, onSidebarToggle, onCloseInternal])

  // Close sidebar when a chat is selected on mobile
  useEffect(() => {
    if (isMobile && selectedChatId && !isExternallyControlled) {
      onCloseInternal()
    }
  }, [selectedChatId, isMobile, onCloseInternal, isExternallyControlled])

  // Auto-select internal data agent when agents are loaded and no agent is currently selected
  useEffect(() => {
    if (!isLoadingAgents && agents.length > 0 && !agentId && onSelectAgent) {
      // Prioritize TIPP agent first
      const internalDataAgent = agents.find(agent => agent.slug === 'internal_data' && agent.type === 'privileged')
      if (internalDataAgent) {
        onSelectAgent(internalDataAgent.id)
      } else {
        // Fallback to GPT agent if TIPP is not available
        const gptAgent = agents.find(agent => agent.slug === 'gpt' && agent.type === 'privileged')
        if (gptAgent) {
          onSelectAgent(gptAgent.id)
        }
      }
    }
  }, [isLoadingAgents, agents, agentId, onSelectAgent])

  const handleChatItemClick = useCallback(
    (chatId: string) => {
      const chat = chats.find(c => c.id === chatId)

      if (chat && chat?.agentId) {
        onSelectChat(chatId, chat.agentId)
      } else {
        // Legacy chat without agent
        onSelectChat(chatId)
      }
      closeSidebarOnMobile()
    },
    [onSelectChat, chats, closeSidebarOnMobile],
  )

  const handleDeleteChat = useCallback(
    (chatId: string) => {
      deleteConversation(chatId, {
        onSuccess: () => {
          // Deleting the open conversation would leave a dangling view, so fall back to a fresh chat.
          if (selectedChatId === chatId) {
            onNewChat()
          }
        },
      })
    },
    [deleteConversation, selectedChatId, onNewChat],
  )

  const handleAgentItemClick = useCallback(
    (agentClickedId: string) => {
      if (agentClickedId !== agentId) {
        onClearConversation?.()
        onSelectAgent?.(agentClickedId)
      }
      closeSidebarOnMobile()
    },
    [agentId, onClearConversation, onSelectAgent, closeSidebarOnMobile],
  )

  // Improved section mapping based on departments
  const agentSections = useMemo(() => {
    const sections = {
      favorites: [] as typeof agents,
      privileged: [] as typeof agents,
      departments: {} as Record<
        string,
        {
          agents: typeof agents
          icon_url?: string
        }
      >,
    }

    agents.forEach(agent => {
      // Add to favorites if preferred
      if (agent.is_preferred) {
        sections.favorites.push(agent)
      }

      // Add to privileged section
      if (agent.type === 'privileged') {
        sections.privileged.push(agent)
      } else {
        // Add to department sections for non-privileged agents
        const department = agent.department
        const departmentName = department?.name || 'Uncategorized'

        if (!sections.departments[departmentName]) {
          sections.departments[departmentName] = {
            agents: [],
            icon_url: department?.icon_url,
          }
        }
        sections.departments[departmentName].agents.push(agent)
      }
    })

    return sections
  }, [agents])

  // Group chats by time period
  const groupedChats = useMemo(() => {
    const groups = {
      today: [] as ChatSummarySchema[],
      thisWeek: [] as ChatSummarySchema[],
      lastWeek: [] as ChatSummarySchema[],
      earlier: [] as ChatSummarySchema[],
    }

    chats.forEach(chat => {
      const period = getChatTimePeriod(chat.timestamp)
      groups[period].push(chat)
    })

    // Sort chats within each group by timestamp (newest first)
    Object.keys(groups).forEach(key => {
      groups[key as keyof typeof groups].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      )
    })

    return groups
  }, [chats])

  // Get section title and count
  const getSectionTitle = useCallback(
    (section: string, count: number) => {
      switch (section) {
        case 'today':
          return `${t('sidebar.today')} (${count})`
        case 'thisWeek':
          return `${t('sidebar.thisWeek')} (${count})`
        case 'lastWeek':
          return `${t('sidebar.lastWeek')} (${count})`
        case 'earlier':
          return `${t('sidebar.earlier')} (${count})`
        default:
          return `${section} (${count})`
      }
    },
    [t],
  )

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }, [])

  const handleLibraryClick = useCallback(() => {
    navigate('/library')
    closeSidebarOnMobile()
  }, [navigate, closeSidebarOnMobile])

  // Initialize intersection observer for infinite scroll using react-intersection-observer
  const { ref: inViewRef, inView } = useInView({
    threshold: 0.1,
    rootMargin: '200px',
  })

  // Privileged agents are shown inline above the chat history when the
  // deployment enables the agents feature.
  const showAssistantsTab = CLIENT_CONFIG.features.agents === true

  // Trigger fetchNextPage when the sentinel element comes into view
  React.useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <>
      <Flex
        {...props}
        as='aside'
        direction='column'
        h='100dvh'
        pt={12} // Increased padding to make room for toggle button
        bg={sidebarBg}
        borderRight='1px'
        borderColor={borderColor}
        // Rounded only as an overlay drawer (mobile); flush when docked beside the chat.
        borderTopRightRadius={isMobile ? 'md' : 0}
        borderBottomRightRadius={isMobile ? 'md' : 0}
        position='fixed'
        left={0}
        top={0}
        zIndex={1000}
        overflow='hidden'
        w={isMobile ? '85vw' : '300px'}
        maxW={isMobile ? '85vw' : '300px'}
        minW={isMobile ? '85vw' : '300px'}
        // -100% hides it fully regardless of width (85vw on mobile, 300px docked).
        transform={isOpen ? 'translateX(0)' : 'translateX(-100%)'}
        transition='transform 0.3s ease-in-out'
      >
        <Button
          bg={CLIENT_CONFIG.theme.colors.primary}
          color='white'
          _hover={{
            bg: 'brand.600',
          }}
          height='40px'
          display='flex'
          alignItems='center'
          justifyContent={isMinimized ? 'center' : 'flex-start'}
          onClick={() => {
            onNewChat()
            closeSidebarOnMobile()
          }}
          isLoading={isLoading}
          loadingText={t('common.startingNewChat')}
          mx={SIDEBAR_SPACING.base}
          px={isMinimized ? 0 : 2}
        >
          <PlusIcon size={16} />
          {!isMinimized && <Text ml={2}>{t('common.newChat')}</Text>}
        </Button>

        <Divider mb={4} />

        <Flex
          direction='column'
          flex='1'
          minH={0}
          overflowY='auto'
          mx={SIDEBAR_SPACING.base}
        >
          {/* Privileged agents */}
          {showAssistantsTab && (
            <>
              {!isMinimized && (
                <Text
                  fontSize='xs'
                  fontWeight='semibold'
                  textTransform='uppercase'
                  letterSpacing='wider'
                  color={countColor}
                  mb={2}
                >
                  {t('sidebar.assistants')}
                </Text>
              )}
              <VStack align='stretch' spacing={1}>
                {isLoadingAgents ? (
                  <Flex justify='center' align='center' py={4}>
                    <Spinner size='sm' color='primary' />
                    <Text ml={2} fontSize='sm' color='gray.500'>
                      {t('sidebar.loadingPrivilegedAgents', { defaultValue: 'Loading...' })}
                    </Text>
                  </Flex>
                ) : agentError ? (
                  <Text py={2} color='red.500'>
                    {agentError}
                  </Text>
                ) : agentSections.privileged.length > 0 ? (
                  [...agentSections.privileged]
                    .sort((a, b) => {
                      const order = { tipp: 0, gpt: 1, web: 2 }
                      return (
                        (order[a.slug as keyof typeof order] ?? 999) - (order[b.slug as keyof typeof order] ?? 999)
                      )
                    })
                    .map(agent => (
                      <SidebarItem
                        key={agent.id}
                        label={agent.name}
                        icon={slugToIcon(agent.slug || '', iconColor, isDarkMode)}
                        isSelected={agent.id === agentId}
                        onClick={() => handleAgentItemClick(agent.id)}
                        showLabel={!isMinimized}
                      />
                    ))
                ) : (
                  <Text py={2}>{t('sidebar.noAssistantsAvailable')}</Text>
                )}
                {/* Favourited (non-privileged) agents appear directly under the
                    always-on privileged agent so users can pin library agents here. */}
                {agentSections.favorites
                  .filter(agent => agent.type !== 'privileged')
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(agent => (
                    <SidebarItem
                      key={agent.id}
                      label={agent.name}
                      icon={getIconByName(agent.profile_picture_url) || slugToIcon(agent.slug || '', iconColor, isDarkMode)}
                      isSelected={agent.id === agentId}
                      onClick={() => handleAgentItemClick(agent.id)}
                      showLabel={!isMinimized}
                    />
                  ))}
                <SidebarItem
                  key='library'
                  label={t('library.library', { defaultValue: 'Library' })}
                  icon={<FiGrid size={16} />}
                  isSelected={false}
                  onClick={handleLibraryClick}
                  showLabel={!isMinimized}
                />
              </VStack>
              <Divider my={4} />
            </>
          )}

          {/* Chat history */}
          <VStack align='stretch' spacing={1}>
                {/* Search input */}
                <InputGroup size='md' mb={4}>
                  <InputLeftElement pointerEvents='none'>
                    <FiSearch color='gray.500' />
                  </InputLeftElement>
                  <Input
                    placeholder={t('sidebar.searchConversations', { defaultValue: 'Search conversations...' } as {
                      defaultValue: string
                    })}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    borderRadius='md'
                    borderColor='gray.300'
                    _placeholder={{
                      color: placeholderColor,
                    }}
                    _focus={{
                      borderColor: 'primary',
                      boxShadow: '0 0 0 1px var(--chakra-colors-primary)',
                    }}
                  />
                </InputGroup>

                {isLoading ? (
                  <Flex justify='center' align='center' height='100px'>
                    <Spinner size='md' color='primary' />
                  </Flex>
                ) : !error && chats.length === 0 && searchQuery.trim().length === 0 ? (
                  <Text color='secondary' mb={4}>
                    {t('sidebar.noConversationsAvailable', { defaultValue: 'No conversations available' } as {
                      defaultValue: string
                    })}
                  </Text>
                ) : !error && chats.length === 0 && searchQuery.trim().length > 0 ? (
                  <Text color='secondaryAlpha'>
                    {t('sidebar.noSearchResults', { defaultValue: 'No conversations matching your search' } as {
                      defaultValue: string
                    })}
                  </Text>
                ) : (
                  <React.Fragment>
                    {/* Today's chats */}
                    {groupedChats.today.length > 0 && (
                      <SidebarSection title={getSectionTitle('today', groupedChats.today.length)}>
                        {groupedChats.today.map((chat: ChatSummarySchema) => (
                          <ConversationRow
                            key={chat.id}
                            chat={chat}
                            icon={getIcon(chat, iconColor, isDarkMode) || <FiMessageSquare size={16} />}
                            onClick={() => handleChatItemClick(chat.id)}
                            isSelected={selectedChatId === chat.id}
                            showLabel={!isMinimized}
                            onRename={(newTitle) => renameConversation({ chatId: chat.id, title: newTitle })}
                            onDelete={() => handleDeleteChat(chat.id)}
                          />
                        ))}
                      </SidebarSection>
                    )}
                    {/* This Week's chats */}
                    {groupedChats.thisWeek.length > 0 && (
                      <SidebarSection title={getSectionTitle('thisWeek', groupedChats.thisWeek.length)}>
                        {groupedChats.thisWeek.map((chat: ChatSummarySchema) => (
                          <ConversationRow
                            key={chat.id}
                            chat={chat}
                            icon={getIcon(chat, iconColor, isDarkMode) || <FiMessageSquare size={16} />}
                            onClick={() => handleChatItemClick(chat.id)}
                            isSelected={selectedChatId === chat.id}
                            showLabel={!isMinimized}
                            onRename={(newTitle) => renameConversation({ chatId: chat.id, title: newTitle })}
                            onDelete={() => handleDeleteChat(chat.id)}
                          />
                        ))}
                      </SidebarSection>
                    )}
                    {/* Last Week's chats */}
                    {groupedChats.lastWeek.length > 0 && (
                      <SidebarSection title={getSectionTitle('lastWeek', groupedChats.lastWeek.length)}>
                        {groupedChats.lastWeek.map((chat: ChatSummarySchema) => (
                          <ConversationRow
                            key={chat.id}
                            chat={chat}
                            icon={getIcon(chat, iconColor, isDarkMode) || <FiMessageSquare size={16} />}
                            onClick={() => handleChatItemClick(chat.id)}
                            isSelected={selectedChatId === chat.id}
                            showLabel={!isMinimized}
                            onRename={(newTitle) => renameConversation({ chatId: chat.id, title: newTitle })}
                            onDelete={() => handleDeleteChat(chat.id)}
                          />
                        ))}
                      </SidebarSection>
                    )}
                    {/* Earlier chats */}
                    {groupedChats.earlier.length > 0 && (
                      <SidebarSection title={getSectionTitle('earlier', groupedChats.earlier.length)}>
                        {groupedChats.earlier.map((chat: ChatSummarySchema) => (
                          <ConversationRow
                            key={chat.id}
                            chat={chat}
                            icon={getIcon(chat, iconColor, isDarkMode) || <FiMessageSquare size={16} />}
                            onClick={() => handleChatItemClick(chat.id)}
                            isSelected={selectedChatId === chat.id}
                            showLabel={!isMinimized}
                            onRename={(newTitle) => renameConversation({ chatId: chat.id, title: newTitle })}
                            onDelete={() => handleDeleteChat(chat.id)}
                          />
                        ))}
                      </SidebarSection>
                    )}

                    {/* Invisible sentinel element for infinite scroll */}
                    {hasNextPage && <Box ref={inViewRef} height='1px' />}

                    {/* Loading indicator for infinite scroll */}
                    {isFetchingNextPage && (
                      <Flex justify='center' align='center' py={4}>
                        <Spinner size='sm' color='primary' />
                        <Text ml={2} fontSize='sm' color='gray.500'>
                          {t('sidebar.loadingMoreConversations', { defaultValue: 'Loading more conversations...' })}
                        </Text>
                      </Flex>
                    )}
                  </React.Fragment>
                )}
          </VStack>
        </Flex>
        <Divider />
        <Box mt='auto' mx={SIDEBAR_SPACING.base} py={2}>
          <VStack spacing={1} align='stretch'>
            {currentUser?.is_superuser && (
              <SidebarItem
                icon={<BarChart3 size={16} />}
                label='Analytics'
                onClick={() => {
                  navigate('/analytics')
                  closeSidebarOnMobile()
                }}
                showLabel={!isMinimized}
              />
            )}
            <SidebarItem
              icon={<Info size={16} />}
              label={t('sidebar.userGuide')}
              onClick={() => {
                openInformationModal()
                closeSidebarOnMobile()
              }}
              showLabel={!isMinimized}
            />
            <SidebarItem
              icon={
                <Avatar
                  size='xs'
                  name={sanitizeNameForInitials(user?.displayName || t('chat.anonymousUser') || 'Anonymous User')}
                  bg='brand.500'
                  color='white'
                />
              }
              label={user?.displayName || t('chat.anonymousUser') || 'Anonymous User'}
              onClick={() => navigate('/settings')}
              showLabel={!isMinimized}
            />
          </VStack>
        </Box>
      </Flex>
      <GeneralInformationModal isOpen={isInformationModalOpen} onClose={closeInformationModal} />
    </>
  )
}

export default React.memo(ChatSidebar)
